import React from 'react';
import {
  Box,
  Button,
  Radio,
} from '@mui/material';
import {
  Document,
  Paragraph,
  TextRun,
  Packer,
  Table as DocxTable,
  TableRow as DocxTableRow,
  TableCell as DocxTableCell,
  WidthType,
} from 'docx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';
import Download from '../../images/managedata/download.svg';
import Pdf from '../../images/managedata/pdf.svg';
import Word from '../../images/managedata/word.svg';
import Csv from '../../images/managedata/csv.svg';

const ExportOptions = ({ reportData, exportFormat, setExportFormat }) => {
  const generateDocx = async (data) => {
    const doc = new Document({
      sections: [
        {
          children: [
            new Paragraph({
              children: [new TextRun({ text: `${data.reportType} Report`, size: 24, bold: true })],
              alignment: 'center',
            }),
            new Paragraph({
              children: [new TextRun(`Total Records: ${data.count || data.data.length}`)],
            }),
            new DocxTable({
              rows: [
                new DocxTableRow({
                  children: data.columns.map((col) => new DocxTableCell({
                    children: [new Paragraph(col.label)],
                  })),
                }),
                ...data.data.map((row) =>
                  new DocxTableRow({
                    children: data.columns.map((col) => new DocxTableCell({
                      children: [new Paragraph(String(row[col.key] || 'NA'))],
                    })),
                  })
                ),
              ],
              width: { size: 100, type: WidthType.PERCENTAGE },
            }),
          ],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${data.reportType}_report.docx`);
  };

  const generatePDF = (data) => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(`${data.reportType} Report`, 10, 10);
    doc.setFontSize(12);
    doc.text(`Total Records: ${data.count || data.data.length}`, 10, 20);

    const head = [data.columns.map((col) => col.label)];
    const body = data.data.map((row) =>
      data.columns.map((col) => String(row[col.key] || 'NA'))
    );

    autoTable(doc, {
      head,
      body,
      startY: 30,
    });

    doc.save(`${data.reportType}_report.pdf`);
  };

  const generateCSV = (data) => {
    const csvData = data.data.map((row) => {
      const rowData = {};
      data.columns.forEach((col) => {
        rowData[col.label] = row[col.key] || 'NA';
      });
      return rowData;
    });

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${data.reportType}_report.csv`);
  };

  const handleExport = () => {
    if (!reportData) return;
    switch (exportFormat) {
      case 'docx':
        generateDocx(reportData);
        break;
      case 'pdf':
        generatePDF(reportData);
        break;
      case 'csv':
        generateCSV(reportData);
        break;
      default:
        break;
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: 1,
        padding: { xs: '8px 12px', sm: '10px 18px', md: '8px 20px' },
        height: { xs: 'auto', sm: '50px', md: '60px' },
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'center',
        gap: { xs: 1, sm: 1.5, md: 2 },
        width: { xs: '100%', sm: 'auto' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 1.5, md: 2 },
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          justifyContent: { xs: 'center', sm: 'flex-start' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Radio
            checked={exportFormat === 'pdf'}
            onChange={() => setExportFormat('pdf')}
            value="pdf"
            sx={{
              color: '#183059',
              '&.Mui-checked': { color: '#20A8E0' },
              padding: 0,
              '& .MuiSvgIcon-root': { fontSize: { xs: '16px', sm: '18px', md: '20px' } },
            }}
          />
          <img
            src={Pdf}
            alt="PDF"
            style={{
              width: window.innerWidth < 600 ? 18 : window.innerWidth < 900 ? 20 : 24,
              height: window.innerWidth < 600 ? 18 : window.innerWidth < 900 ? 20 : 24,
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Radio
            checked={exportFormat === 'csv'}
            onChange={() => setExportFormat('csv')}
            value="csv"
            sx={{
              color: '#183059',
              '&.Mui-checked': { color: '#20A8E0' },
              padding: 0,
              '& .MuiSvgIcon-root': { fontSize: { xs: '16px', sm: '18px', md: '20px' } },
            }}
          />
          <img
            src={Csv}
            alt="CSV"
            style={{
              width: window.innerWidth < 600 ? 18 : window.innerWidth < 900 ? 20 : 24,
              height: window.innerWidth < 600 ? 18 : window.innerWidth < 900 ? 20 : 24,
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Radio
            checked={exportFormat === 'docx'}
            onChange={() => setExportFormat('docx')}
            value="docx"
            sx={{
              color: '#183059',
              '&.Mui-checked': { color: '#20A8E0' },
              padding: 0,
              '& .MuiSvgIcon-root': { fontSize: { xs: '16px', sm: '18px', md: '20px' } },
            }}
          />
          <img
            src={Word}
            alt="DOCX"
            style={{
              width: window.innerWidth < 600 ? 18 : window.innerWidth < 900 ? 20 : 24,
              height: window.innerWidth < 600 ? 18 : window.innerWidth < 900 ? 20 : 24,
            }}
          />
        </Box>
      </Box>
      <Button
        variant="contained"
        onClick={handleExport}
        sx={{
          gap: { xs: '4px', sm: '8px', md: '10px' },
          borderRadius: '8px',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: '#414958',
          paddingTop: { xs: '4px', sm: '6px', md: '8px' },
          paddingRight: { xs: '8px', sm: '10px', md: '12px' },
          paddingBottom: { xs: '4px', sm: '6px', md: '8px' },
          paddingLeft: { xs: '8px', sm: '10px', md: '12px' },
          backgroundColor: '#F9E719',
          color: 'black',
          fontSize: { xs: '8px', sm: '9px', md: '14px' },
          textTransform: 'none',
          '&:hover': { backgroundColor: '#FFD700' },
        }}
      >
        <img
          src={Download}
          alt="download"
          style={{
            marginRight: '8px',
            width: window.innerWidth < 600 ? '14px' : '16px',
            height: window.innerWidth < 600 ? '14px' : '16px',
          }}
        />
        Download
      </Button>
    </Box>
  );
};

export default ExportOptions;